import React, { useEffect, useMemo } from 'react'
import cn, { Argument as ClassName } from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { Button } from 'primereact/button'

import { Dropdown } from '../../uikit/Dropdown'
import { useCompanyGetCompanySettingsQuery, useCompanyUpdateCompanySettingsMutation } from '../../redux/api/web-api'
import {
    AVAILABLE_LANGUAGES,
    AVAILABLE_LANGUAGES_LOCAL,
    useLocalizationContext,
} from '../../components/localization-provider'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'
import { WidgetLayout } from '../../../lib/widget-layout'
import {
    DEFAULT_BILLING_COUNTRY,
    getCompanySettingValue,
    mapOrganisationSettingsToCompanySettings,
    type OrganisationSettingsValues,
} from '../../utils/company-settings'
import { getNamesWithFallback } from '../../../i18n/countries'

export type OrganisationSettingsWidgetProps = {
    className?: ClassName
    style?: React.CSSProperties
    companyId?: string
    disabled?: boolean
}

const getCommunicationLanguage = (settings: Parameters<typeof getCompanySettingValue>[0]): string =>
    getCompanySettingValue(settings, 'Language') ?? ''

const getBillingCountry = (settings: Parameters<typeof getCompanySettingValue>[0]): string =>
    getCompanySettingValue(settings, 'BillingCountry') ?? DEFAULT_BILLING_COUNTRY

export function OrganisationSettingsWidget({
    className,
    style,
    companyId,
    disabled,
}: OrganisationSettingsWidgetProps): React.ReactElement {
    const text = useText()
    const toast = useToast()
    const { locale } = useLocalizationContext()
    const communicationLanguageOptions = useMemo(
        () =>
            AVAILABLE_LANGUAGES.map((key) => ({
                key,
                value: AVAILABLE_LANGUAGES_LOCAL[key] ?? key,
            })),
        []
    )
    const billingCountryOptions = useMemo(
        () =>
            Object.entries(getNamesWithFallback(locale))
                .map(([key, value]) => ({ key, value }))
                .sort((a, b) => a.value.localeCompare(b.value, locale)),
        [locale]
    )
    const { data: companySettings, isFetching } = useCompanyGetCompanySettingsQuery(
        { companyId: companyId ?? '' },
        { skip: !companyId }
    )
    const [updateCompanySettings, { isLoading: isSaving }] = useCompanyUpdateCompanySettingsMutation()

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty },
    } = useForm<OrganisationSettingsValues>({
        defaultValues: {
            communicationLanguage: getCommunicationLanguage(companySettings),
            billingCountry: getBillingCountry(companySettings),
        },
        mode: 'onChange',
    })

    useEffect(() => {
        reset({
            communicationLanguage: getCommunicationLanguage(companySettings),
            billingCountry: getBillingCountry(companySettings),
        })
    }, [companySettings, reset])

    const saveSettings = handleSubmit(async ({ communicationLanguage, billingCountry }) => {
        if (!companyId || !communicationLanguage || !billingCountry) return

        try {
            await updateCompanySettings({
                companyId,
                body: mapOrganisationSettingsToCompanySettings({ communicationLanguage, billingCountry }),
            }).unwrap()
            reset({ communicationLanguage, billingCountry })
            toast.notifySuccess(text('msgCompanyUpdated'))
        } catch {
            toast.notifyError(text('msgCompanyNotUpdated'))
        }
    })

    return (
        <WidgetLayout
            className={cn(className)}
            style={style}
            title={text('lblOrganisationSettingsWidgetTitle')}
            data-testid="organisation-settings-widget"
        >
            <form className="flex flex-column gap-3" onSubmit={saveSettings} noValidate>
                <div>
                    <label htmlFor="language" data-testid="language-label">
                        {text('lblCommunicationLanguage')}
                    </label>
                    <Controller
                        name="communicationLanguage"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Dropdown
                                id="language"
                                aria-label={text('lblCommunicationLanguage')}
                                options={communicationLanguageOptions}
                                optionLabel="value"
                                optionValue="key"
                                disabled={disabled || isFetching}
                                showClear={false}
                                value={field.value}
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                </div>

                <div>
                    <label htmlFor="billing-country" data-testid="billing-country-label">
                        {text('lblBillingCountry')}
                    </label>
                    <Controller
                        name="billingCountry"
                        control={control}
                        rules={{ required: true }}
                        render={({ field }) => (
                            <Dropdown
                                id="billing-country"
                                aria-label={text('lblBillingCountry')}
                                options={billingCountryOptions}
                                optionLabel="value"
                                optionValue="key"
                                disabled={disabled || isFetching}
                                showClear={false}
                                value={field.value}
                                onChange={(event) => field.onChange(event.value)}
                                onBlur={field.onBlur}
                            />
                        )}
                    />
                </div>

                <div className="flex justify-content-end">
                    <Button
                        data-testid="organisation-settings-save-btn"
                        type="submit"
                        disabled={disabled || isFetching || isSaving || !isDirty}
                        label={text('btnSave')}
                        icon="pi pi-save"
                    />
                </div>
            </form>
        </WidgetLayout>
    )
}
